import type { DeckObject, UserData } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { addSentryDiagnosticBreadcrumb, trackError } from "../../utils/analytics/track-error";
import {
  QuestionGenerationError,
  QuestionGenerationStage,
  buildQuestionGenerationCorrelationTelemetry,
  buildQuestionGenerationFailureTelemetry,
} from "./question-generation-observability";

// The learning journey overview reports without a surface; these pages say which they are
export type QuestionGenerationFailureSurface = "first_set_preview";

export function reportQuestionGenerationFailure({
  error,
  attemptId,
  deck,
  user,
  generatedDeckId,
  sourceDeckId,
  surface,
  additionalTelemetry,
}: {
  error: QuestionGenerationError;
  attemptId: string;
  deck: DeckObject | null | undefined;
  user: UserData;
  generatedDeckId?: string | null;
  sourceDeckId?: string | null;
  /** The page that noticed the failure, when the same failure can be reported from more than one. */
  surface?: QuestionGenerationFailureSurface;
  additionalTelemetry?: Record<string, unknown>;
}): void {
  const telemetry = buildQuestionGenerationFailureTelemetry({
    error,
    attemptId,
    deck,
    generatedDeckId,
    sourceDeckId,
  });
  trackError(error, {
    scope: "question-generation",
    userId: user?.id,
    tags: {
      question_generation_stage: error.stage,
      question_generation_error_code: error.code,
      question_generation_attempt_id: attemptId,
      content_medium_type: String(telemetry.contentMediumType || "unknown"),
      question_generation_worker: String(telemetry.questionGenerationWorker || "unknown"),
      orchestration_modal_function_call_id: String(telemetry.orchestrationModalFunctionCallId || "missing"),
      modal_function_call_id: String(telemetry.modalFunctionCallId || "missing"),
      file_processing_task_id: String(telemetry.fileProcessingTaskId || "missing"),
      generated_deck_id: String(telemetry.generatedDeckId || "missing"),
      ...(surface ? { question_generation_surface: surface } : {}),
    },
    extra: {
      ...telemetry,
      ...additionalTelemetry,
      sentryOrigin: "frontend",
      isOnline: navigator.onLine,
      documentVisibility: document.visibilityState,
    },
  });
}

export function reportQuestionGenerationNetworkRetry({
  error,
  attemptId,
  deck,
  user,
  failedAttemptNumber,
  maximumAttempts,
  sourceDeckId,
}: {
  error: QuestionGenerationError;
  attemptId: string;
  deck: DeckObject | null | undefined;
  user: UserData;
  failedAttemptNumber: number;
  maximumAttempts: number;
  sourceDeckId?: string | null;
}): void {
  const telemetry = buildQuestionGenerationFailureTelemetry({
    error,
    attemptId,
    deck,
    sourceDeckId,
  });
  addSentryDiagnosticBreadcrumb({
    category: "question-generation",
    message: "Question generation request retry scheduled",
    data: {
      ...telemetry,
      outcome: "retrying",
      userId: user?.id || null,
      stage: error.stage,
      errorCode: error.code,
      failedAttemptNumber,
      maximumAttempts,
      isOnline: navigator.onLine,
      documentVisibility: document.visibilityState,
    },
  });
}

export function reportQuestionGenerationStillActive({
  attemptId,
  deck,
  user,
  generatedDeckId,
  elapsedMonitoringMs,
  nextStatusCheckDelayMs,
  maximumMonitoringMs,
}: {
  attemptId: string;
  deck: DeckObject | null | undefined;
  user: UserData;
  generatedDeckId: string;
  elapsedMonitoringMs: number;
  nextStatusCheckDelayMs: number;
  maximumMonitoringMs: number;
}): void {
  const telemetry = buildQuestionGenerationCorrelationTelemetry({
    attemptId,
    deck,
    generatedDeckId,
  });
  trackError("Question generation remains active after the initial client wait", {
    scope: "question-generation-active-wait",
    level: "warning",
    fingerprint: ["question-generation-active-wait"],
    userId: user?.id,
    tags: {
      question_generation_stage: QuestionGenerationStage.AwaitWorker,
      question_generation_wait_outcome: "worker-still-active",
      question_generation_attempt_id: attemptId,
      content_medium_type: String(telemetry.contentMediumType || "unknown"),
      question_generation_worker: String(telemetry.questionGenerationWorker || "unknown"),
      orchestration_modal_function_call_id: String(telemetry.orchestrationModalFunctionCallId || "missing"),
      modal_function_call_id: String(telemetry.modalFunctionCallId || "missing"),
      file_processing_task_id: String(telemetry.fileProcessingTaskId || "missing"),
      generated_deck_id: generatedDeckId,
    },
    extra: {
      ...telemetry,
      outcome: "worker-still-active",
      elapsedMonitoringMs,
      nextStatusCheckDelayMs,
      maximumMonitoringMs,
      sentryOrigin: "frontend",
      isOnline: navigator.onLine,
      documentVisibility: document.visibilityState,
    },
  });
}
